import React, { useEffect, useState } from "react";
import { apiFetch } from "../../api/apiFetch";
import ErrorModal from "../../components/ErrorModal";
import { usePermission } from "../../hooks/usePermission";

function CategoryRow({ config, onSave, onDelete, canManage }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    naam: config.naam,
    keywords: (config.keywords || []).join(", "),
    arts_pct: config.arts_pct,
    praktijk_pct: config.praktijk_pct,
    btw_pct: config.btw_pct,
    inkomstenfile_kolom: config.inkomstenfile_kolom || "",
    volgorde: config.volgorde,
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(config.id, {
        naam: form.naam,
        keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
        arts_pct: parseFloat(form.arts_pct),
        praktijk_pct: parseFloat(form.praktijk_pct),
        btw_pct: parseFloat(form.btw_pct),
        inkomstenfile_kolom: form.inkomstenfile_kolom || null,
        volgorde: parseInt(form.volgorde, 10),
      });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <tr className="bg-yellow-50">
        <td className="px-3 py-2">
          <input
            value={form.volgorde}
            onChange={(e) => setForm({ ...form, volgorde: e.target.value })}
            className="w-14 border border-gray-300 rounded px-2 py-1 text-xs"
            type="number"
          />
        </td>
        <td className="px-3 py-2">
          <input
            value={form.naam}
            onChange={(e) => setForm({ ...form, naam: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
          />
        </td>
        <td className="px-3 py-2">
          <input
            value={form.keywords}
            onChange={(e) => setForm({ ...form, keywords: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
            placeholder="keyword1, keyword2"
          />
        </td>
        <td className="px-3 py-2">
          <input
            value={form.arts_pct}
            onChange={(e) => setForm({ ...form, arts_pct: e.target.value })}
            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
            type="number"
            step="0.01"
            min="0"
            max="1"
          />
        </td>
        <td className="px-3 py-2">
          <input
            value={form.praktijk_pct}
            onChange={(e) => setForm({ ...form, praktijk_pct: e.target.value })}
            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
            type="number"
            step="0.01"
            min="0"
            max="1"
          />
        </td>
        <td className="px-3 py-2">
          <input
            value={form.btw_pct}
            onChange={(e) => setForm({ ...form, btw_pct: e.target.value })}
            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs"
            type="number"
            step="0.01"
            min="0"
            max="1"
          />
        </td>
        <td className="px-3 py-2">
          <input
            value={form.inkomstenfile_kolom}
            onChange={(e) => setForm({ ...form, inkomstenfile_kolom: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1 text-xs"
          />
        </td>
        <td className="px-3 py-2 whitespace-nowrap">
          <button
            onClick={handleSave}
            disabled={saving}
            className="text-xs text-blue-600 font-medium hover:text-blue-800 mr-2"
          >
            {saving ? "..." : "Opslaan"}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Annuleren
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr className="hover:bg-gray-50 border-b border-gray-100">
      <td className="px-3 py-2 text-xs text-gray-500">{config.volgorde}</td>
      <td className="px-3 py-2 text-sm font-medium text-gray-800">{config.naam}</td>
      <td className="px-3 py-2 text-xs text-gray-600">
        {(config.keywords || []).join(", ")}
      </td>
      <td className="px-3 py-2 text-xs text-right text-gray-700">
        {(parseFloat(config.arts_pct) * 100).toFixed(0)}%
      </td>
      <td className="px-3 py-2 text-xs text-right text-gray-700">
        {(parseFloat(config.praktijk_pct) * 100).toFixed(0)}%
      </td>
      <td className="px-3 py-2 text-xs text-right text-gray-700">
        {(parseFloat(config.btw_pct) * 100).toFixed(0)}%
      </td>
      <td className="px-3 py-2 text-xs text-gray-500 font-mono">
        {config.inkomstenfile_kolom || "—"}
      </td>
      <td className="px-3 py-2 whitespace-nowrap">
        {canManage && (
          <>
            <button
              onClick={() => setEditing(true)}
              className="text-xs text-blue-600 hover:text-blue-800 mr-2"
            >
              Bewerken
            </button>
            <button
              onClick={() => onDelete(config.id)}
              className="text-xs text-red-500 hover:text-red-700"
            >
              Verwijderen
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

export default function ResultsMemberConfig({ user }) {
  const { hasPermission } = usePermission();
  const canManage = hasPermission("results.manage");

  const [members, setMembers] = useState([]);
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ show: false, message: "" });

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const data = await apiFetch("/results/members");
      setMembers(data);
      // Fetch categories for each member
      const catData = {};
      await Promise.all(
        data.map(async (m) => {
          const cats = await apiFetch(`/results/members/${m.id}/categories`);
          catData[m.id] = cats;
        })
      );
      setCategories(catData);
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij ophalen van configuratie." });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async (configId, data) => {
    try {
      await apiFetch(`/results/members/categories/${configId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      await fetchMembers();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij opslaan." });
      throw err;
    }
  };

  const handleDeleteCategory = async (configId) => {
    if (!window.confirm("Categorie verwijderen?")) return;
    try {
      await apiFetch(`/results/members/categories/${configId}`, { method: "DELETE" });
      await fetchMembers();
    } catch (err) {
      setErrorModal({ show: true, message: err.message || "Fout bij verwijderen." });
    }
  };

  const TYPE_LABELS = {
    mediris: "Mediris",
    ontvangstenboek: "Ontvangstenboek",
    huurder: "Huurder",
  };

  return (
    <div className="p-4 sm:p-6 max-w-full">
      {errorModal.show && (
        <ErrorModal
          message={errorModal.message}
          onClose={() => setErrorModal({ show: false, message: "" })}
        />
      )}

      <h1 className="text-2xl font-bold text-gray-800 mb-6">Configuratie medewerkers</h1>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-8">
          {members.map((member) => (
            <div key={member.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {/* Member header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-semibold text-gray-800">{member.naam}</h2>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-500">
                      {TYPE_LABELS[member.type] || member.type}
                    </span>
                    {member.rrn && (
                      <span className="text-xs text-gray-400 font-mono">{member.rrn}</span>
                    )}
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        member.is_active
                          ? "bg-green-100 text-green-700"
                          : "bg-gray-100 text-gray-500"
                      }`}
                    >
                      {member.is_active ? "Actief" : "Inactief"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Category configs */}
              {(categories[member.id] || []).length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100">
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Volgorde</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Naam</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Keywords</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Arts %</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Praktijk %</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">BTW %</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Kolom</th>
                        <th className="px-3 py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {(categories[member.id] || []).map((config) => (
                        <CategoryRow
                          key={config.id}
                          config={config}
                          onSave={handleSaveCategory}
                          onDelete={handleDeleteCategory}
                          canManage={canManage}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-5 py-6 text-sm text-gray-400">
                  Geen categorie-configuraties.
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
